import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vehicleFuelTypeApi } from "../../../modules/vehicle/fueltype/api";
import {
  CertificateFuelTypeFilterPageRequest,
  CreateUpdateCertificateFuelType,
} from "../../../modules/vehicle/fueltype/types";
import { EntityObject } from "../../types";

export const VEHICLE_FUEL_TYPE_QUERY_KEYS = {
  filterVehicleFuelTypes: (payload?: CertificateFuelTypeFilterPageRequest) =>
    payload ? ["filterVehicleFuelTypes", payload] : ["filterVehicleFuelTypes"],
  createVehicleFuelType: () => ["createVehicleFuelType"],
  updateVehicleFuelType: () => ["updateVehicleFuelType"],
  deleteVehicleFuelType: () => ["deleteVehicleFuelType"],
};

export const useFilterVehicleFuelTypes = (payload: CertificateFuelTypeFilterPageRequest) => {
  return useQuery({
    queryKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.filterVehicleFuelTypes(payload),
    queryFn: async () => {
      const { data } = await vehicleFuelTypeApi.filterVehicleFuelTypes(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateVehicleFuelType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.createVehicleFuelType(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCertificateFuelType) => {
      const { data } = await vehicleFuelTypeApi.createVehicleFuelType(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.filterVehicleFuelTypes() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateVehicleFuelType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.updateVehicleFuelType(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCertificateFuelType>) => {
      const { data } = await vehicleFuelTypeApi.updateVehicleFuelType(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.filterVehicleFuelTypes() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteVehicleFuelType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.deleteVehicleFuelType(),
    mutationFn: (id: string) => {
      return vehicleFuelTypeApi.deleteVehicleFuelType({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_FUEL_TYPE_QUERY_KEYS.filterVehicleFuelTypes() });
      Notification.itemDeletedNotification();
    },
  });
};
